import React, { useState } from 'react'
import Image from '@corratech/pylot-image'
import { AlfaRichContent as RichContent } from '@components/alfakher/pylot-rich-content'
import { Button } from '@corratech/pylot-ui'
import { FlavoursTileProps } from '../../lib/type'
import { QuantitySelector } from '@components/QuantitySelector/QuantitySelector'
import { useTranslation } from 'next-i18next'
import s from '@components/common/ProductTile/ProductTile.module.scss'
import ds from './Flavours.module.scss'
import cn from 'classnames'
import Price from '@corratech/pylot-custom-kit-flow/src/Components/Price/Price'
import type { SimpleProduct } from '@pylot-data/pylotschema'

export const FlavoursTile = ({
    option,
    flavour,
    selectedOptions,
    selectedFlavours,
    setSelectedOptions,
    disableAddingFlavours = false,
    numberOfAvailablePods = 1,
    isOutOfStock = false,
    selectedPods = [],
    qty: defaultQuantity = 0,
    isFavourite,
    favorite,
    setFavorite,
    mappedFlavours,
    isSubscribed,
    isParentProductIsFlavourKit
}: FlavoursTileProps): JSX.Element => {
    const { t } = useTranslation(['common', 'plp'])

    const product = option?.product as SimpleProduct & {
        custom_kit_short_description?: string
    }
    const image = product?.thumbnail
    const [qty, setQty] = useState(defaultQuantity)
    const handleProductTileClickd = (uid?: string): string[] | undefined => {
        if (setSelectedOptions && uid) {
            const modifiedSelectedOptions = selectedOptions?.includes(uid!)
                ? selectedOptions.filter((option) => option != uid)
                : selectedOptions
            const existingDevice = flavour?.options?.find((flavourItem) => {
                return (
                    selectedOptions?.includes(String(flavourItem?.uid)) &&
                    flavourItem?.uid === uid
                )
            })
            if (existingDevice) {
                if (existingDevice.uid == uid) {
                    setSelectedOptions(modifiedSelectedOptions!)
                    return selectedOptions
                }
            }
            setSelectedOptions([...(modifiedSelectedOptions ?? []), uid])
        }
    }
    const currentFlavourSelected = option?.uids?.filter((uid: string) => {
        return selectedOptions?.includes(uid)
    })

    const numberOfCurrentAvailableFlavours =
        (currentFlavourSelected?.length ?? 0) - (option?.uids?.length ?? 0)
    const numberOfSelectedFlavours = selectedFlavours?.length ?? 0
    const currentAvailablePods =
        numberOfAvailablePods - numberOfSelectedFlavours
    const availablePod = option?.pods?.find(
        (pod) => !selectedPods.includes(pod)
    )
    const getButtonLabel = () => {
        if (isButtonDisabled) {
            if (isOutOfStock) {
                return <span className="text-xs">{t('SOLD OUT')}</span>
            }
            return <span className={ds['plus-icon']}> {t('+')} </span>
        }
        return t('+')
    }
    const isButtonDisabled =
        disableAddingFlavours ||
        numberOfCurrentAvailableFlavours >= currentAvailablePods ||
        !option?.pods?.includes(numberOfSelectedFlavours)! ||
        typeof availablePod == 'undefined'

    const getButton = () => {
        return (
            <Button
                type="button"
                variant={isOutOfStock ? 'flat' : 'rounded'}
                className={cn(
                    ds['flavour-button'],
                    isOutOfStock ? ds['flavour-button-OOS'] : ''
                )}
                onClick={() => {
                    setQty(1)
                    handleProductTileClickd(
                        option?.uids?.[numberOfSelectedFlavours] ??
                            option?.uids?.[availablePod ?? 0]
                    )
                }}
                disabled={isButtonDisabled}
            >
                {getButtonLabel()}
            </Button>
        )
    }
    const handleFavoriteSelection = () => {
        if (isFavourite && setFavorite && product?.sku) {
            let filteredOptions = selectedOptions
            const flavour = mappedFlavours?.find(
                (flavour) => flavour?.key == product?.sku
            )
            if (favorite) {
                const prevFlavour = mappedFlavours?.find(
                    (flavour) => flavour.key === favorite
                )
                filteredOptions = filteredOptions?.filter(
                    (option) => !prevFlavour?.uids?.includes(option)
                )
            }
            if (favorite === product.sku) {
                setFavorite(null)
            } else if (flavour && filteredOptions) {
                setFavorite(product.sku)
                filteredOptions = [...filteredOptions, ...flavour.uids]
            }
            if (setSelectedOptions) {
                setSelectedOptions([...(filteredOptions ?? [])])
            }
        }
    }

    return (
        <div
            className={cn(
                s['productitem-block'],
                ds['productitem-block'],
                isFavourite ? ds['isFavourite'] : '',
                favorite === product?.sku ? ds['active-flavour'] : '',
                isOutOfStock ? ds['flavour-OOS'] : ''
            )}
            tabIndex={isOutOfStock ? -1 : 0}
            role="button"
            aria-disabled={isOutOfStock}
            onKeyDown={isOutOfStock ? undefined : handleFavoriteSelection}
            onClick={isOutOfStock ? undefined : handleFavoriteSelection}
        >
            {isOutOfStock && (
                <div
                    className={cn(s['product-eyebrow'], ds['product-eyebrow'])}
                >
                    <span className={cn('default-badge')}>{t('SOLD OUT')}</span>
                </div>
            )}
            <div className={cn('productimage-block')}>
                <div className={ds['filter-tittle']}>
                    {product?.is_tobacco_product === '1'
                        ? t('tobacco')
                        : t('nicotine-free')}
                </div>
                <div
                    className={cn(
                        s['productimg-link'],
                        ds['productimg-link'],
                        'product-card-image-swipe',
                        'slider-product-img'
                    )}
                >
                    <Image
                        src={image?.url!}
                        alt={option?.product?.name!}
                        width={390}
                        height={390}
                        className={s['product-img']}
                    />
                </div>
            </div>
            <div
                className={cn(
                    s['plp-prod-details'],
                    'sliderproduct-detail',
                    ds['plp-prod-details']
                )}
            >
                <p className={cn(ds['product-name'])} title={product?.name!}>
                    {product?.name}
                </p>
                <p className={cn(ds['short-description'])}>
                    <RichContent
                        html={
                            product?.custom_kit_short_description ??
                            product?.short_description?.html
                        }
                    />
                </p>
            </div>
            <div>
                {!isParentProductIsFlavourKit && (
                    <Price
                        isSubscribed={isSubscribed}
                        product={option?.product!}
                    />
                )}
            </div>
            {!isFavourite ? (
                <div className={ds['button-container']}>
                    {!selectedOptions?.find((selectedOption) =>
                        option?.uids?.includes(selectedOption)
                    ) ? (
                        getButton()
                    ) : (
                        <div className="pdp-qty">
                            <QuantitySelector
                                quantity={qty}
                                // @ts-ignore this is added as store folders are not tracked by ts
                                isAddingDisabled={
                                    (disableAddingFlavours &&
                                        option?.uids?.length >=
                                            numberOfAvailablePods) ||
                                    disableAddingFlavours ||
                                    qty >= option?.uids?.length ||
                                    typeof availablePod == 'undefined'
                                }
                                // @ts-ignore this is added as store folders are not tracked by ts
                                customMin
                                min={0}
                                setQuantity={(quantity) => {
                                    const isAdding = quantity > qty
                                    const quant = (quantity as number) ?? 0
                                    setQty(quantity)

                                    let modifiedSelectedOptions = [
                                        ...selectedOptions
                                    ]
                                    if (quantity === 0) {
                                        modifiedSelectedOptions = modifiedSelectedOptions.filter(
                                            (selectedOption) =>
                                                !option?.uids?.includes(
                                                    selectedOption
                                                )
                                        )
                                    } else {
                                        const uid =
                                            option?.uids?.[
                                                availablePod ??
                                                    numberOfSelectedFlavours
                                            ]
                                        const existingUid = option?.uids?.find(
                                            (uid) =>
                                                selectedOptions?.includes(uid)
                                        )
                                        if (isAdding) {
                                            modifiedSelectedOptions.push(uid)
                                        } else {
                                            modifiedSelectedOptions = modifiedSelectedOptions?.filter(
                                                (uid: string): boolean =>
                                                    existingUid !== uid
                                            )
                                        }
                                    }

                                    if (setSelectedOptions) {
                                        setSelectedOptions(
                                            modifiedSelectedOptions
                                        )
                                    }
                                }}
                            />
                        </div>
                    )}
                </div>
            ) : (
                ''
            )}
        </div>
    )
}
export default FlavoursTile
